import React, { createContext, useContext, useEffect, useReducer } from 'react'

import pickBy from 'lodash/pickBy'
import router, { useRouter } from 'next/router'
import { createSelector } from 'reselect'

import filterContext from '@/components/pages/production/components/context'
import ListingsContext from '@/context/listings/context'
import type { ComposedListing, ListingsGroup } from '@/types/app-types'
import { queryParamToStr } from '@/utils/query-strings'

enum ActionType {
    TOGGLE_DETAILS,
    UPDATE_QUANTITY,
    SET_IS_ROUTE_TRANSITIONING,
}

interface ToggleDetailsAction {
    type: ActionType.TOGGLE_DETAILS
    payload: {
        listing: ComposedListing | null
        listingGroup?: ListingsGroup
    }
}

interface UpdateQuantityAction {
    type: ActionType.UPDATE_QUANTITY
    payload: {
        newQuantity: number
    }
}

interface SetIsRouteTransitioningAction {
    type: ActionType.SET_IS_ROUTE_TRANSITIONING
    payload: {
        isRouteTransitioning: boolean
    }
}

interface State {
    /**
     * 2 possible states of a listing:
     * - *ComposedListing* - ticket details should be displayed;
     * - *null* - ticket details should be hidden;
     */
    listing: ComposedListing | null
    listingGroup: ListingsGroup | null
    isRouteTransitioning: boolean
}

interface Dispatch {
    toggleDetails: (payload: { listing: ComposedListing | null; listingGroup?: ListingsGroup }) => void
    updateQuantity: (payload: { newQuantity: string }) => void
}

interface ContextValue {
    state: State
    dispatch: Dispatch
}

const defaultContextValue: ContextValue = {
    state: {
        listing: null,
        listingGroup: null,
        isRouteTransitioning: false,
    },
    dispatch: {
        toggleDetails: () => undefined,
        updateQuantity: () => undefined,
    },
}

const context = createContext(defaultContextValue)

const toggleDetails = ({
    dispatch,
    listing,
    listingGroup,
    targetQuantity,
}: {
    dispatch: React.Dispatch<Action>
    listing: ComposedListing | null
    listingGroup?: ListingsGroup
    targetQuantity?: number
}) => {
    setIsRouteTransitioning(dispatch, true)
    if (listing) {
        const { quantityOptionsList } = listing

        let defaultQuantity: number | undefined

        // Firstly, check target quantity
        // Its value might be passed in 'qty' query string parameter when user navigates to ticket details via direct URL
        // Or it might come from the quantity filter
        if (targetQuantity && quantityOptionsList.includes(targetQuantity)) {
            defaultQuantity = targetQuantity
        }

        // Lastly, fallback to the first quantity option
        if (!defaultQuantity) {
            defaultQuantity = quantityOptionsList[0]
        }

        if (listing.defaultQuantity !== defaultQuantity) {
            listing = { ...listing, defaultQuantity }
        }

        dispatch({ type: ActionType.TOGGLE_DETAILS, payload: { listing, listingGroup } })
    } else {
        dispatch({ type: ActionType.TOGGLE_DETAILS, payload: { listing: null } })
    }
}

const updateQuantity = (
    state: State,
    dispatch: React.Dispatch<Action>,
    pathname: string,
    slug: string | undefined,
    id: string | undefined,
    listingId: string | undefined,
    groupId: string | undefined,
    newQuantity: string,
) => {
    const newQuantityNum = parseInt(newQuantity)

    // Check that new quantity is available for the listing
    const quantities =
        state.listing?.inGroup && !!state.listingGroup
            ? state.listingGroup?.quantityOptionsList
            : state.listing?.quantityOptionsList

    if (quantities?.includes(newQuantityNum)) {
        setIsRouteTransitioning(dispatch, true)
        dispatch({ type: ActionType.UPDATE_QUANTITY, payload: { newQuantity: newQuantityNum } })

        const newListingId =
            state.listing?.inGroup && !!state.listingGroup
                ? state.listingGroup?.bestListingsByQuantity[newQuantityNum].id
                : listingId

        updateUrlParameters({ pathname, slug, id, listingId: newListingId, groupId, quantity: newQuantity })
    }
}

const setIsRouteTransitioning = (
    dispatch: React.Dispatch<SetIsRouteTransitioningAction>,
    isRouteTransitioning: boolean,
) => {
    dispatch({ type: ActionType.SET_IS_ROUTE_TRANSITIONING, payload: { isRouteTransitioning } })
}

type Action = ToggleDetailsAction | UpdateQuantityAction | SetIsRouteTransitioningAction

const useTicketDetailsReducer = (): [State, React.Dispatch<Action>] => {
    return useReducer((state: State, action: Action) => {
        switch (action.type) {
            case ActionType.TOGGLE_DETAILS:
                const { listing, listingGroup } = action.payload
                return { ...state, listing, listingGroup: listingGroup ?? null }
            case ActionType.UPDATE_QUANTITY:
                const { newQuantity } = action.payload

                // If in group, update listing to match best for quantity
                const newListing =
                    state.listing?.inGroup && !!state.listingGroup
                        ? state.listingGroup?.bestListingsByQuantity[newQuantity]
                        : state.listing

                const listingWithNewQuantity = Object.assign({}, newListing, { defaultQuantity: newQuantity })
                return { ...state, listing: listingWithNewQuantity }
            case ActionType.SET_IS_ROUTE_TRANSITIONING:
                const { isRouteTransitioning } = action.payload
                return { ...state, isRouteTransitioning }
            default:
                return state
        }
    }, defaultContextValue.state)
}

export const listingSelector = createSelector(
    (listings: ComposedListing[], listingId: string, groupId?: string) => ({
        listings,
        listingId,
        groupId,
    }),
    ({ listings, listingId, groupId }): [ComposedListing | null, ListingsGroup | undefined] => {
        const listing: ComposedListing | undefined = listings.find(
            (listing: ComposedListing) => listing.id === listingId,
        )

        const group = groupId
            ? (listings.find((listing) => listing.id === groupId) as ListingsGroup | undefined)
            : undefined

        return [listing ?? null, group]
    },
)

const updateUrlParameters = ({
    pathname,
    slug,
    id,
    listingId,
    groupId,
    quantity,
}: {
    pathname: string
    slug: string | undefined
    id: string | undefined
    listingId: string | undefined
    groupId: string | undefined
    quantity: number | string
}) => {
    const baseQuery = pickBy({ slug, id })
    router.push(
        {
            pathname,
            query: {
                ...baseQuery,
                showDetails: listingId,
                groupId,
                qty: quantity.toString(),
            },
        },
        undefined,
        {
            shallow: true,
        },
    )
}

const removeUrlParameters = (pathname: string, slug: string | undefined, id: string | undefined) => {
    const query = pickBy({ slug, id })
    router.push({ pathname, query }, undefined, { shallow: true })
}

export const TicketDetailsProvider: React.FC = ({ children }) => {
    const [state, dispatch] = useTicketDetailsReducer()

    const {
        state: { fetching },
        selectors: { composedListings },
    } = useContext(ListingsContext)

    const { pathname, query, events } = useRouter()
    const slug: string | undefined = queryParamToStr(query.slug)
    const id: string | undefined = queryParamToStr(query.id)
    const queryListingId: string | undefined = queryParamToStr(query.showDetails)
    const queryGroupId: string | undefined = queryParamToStr(query.groupId)
    const queryQuantity: number | undefined = parseInt(queryParamToStr(query.qty) ?? '') || undefined

    const { state: filterState } = useContext(filterContext)
    const filterQuantity: number = filterState.quantityFilterState.quantity

    useEffect(() => {
        const handleRouteChangeComplete = () => setIsRouteTransitioning(dispatch, false)
        events.on('routeChangeComplete', handleRouteChangeComplete)
        return () => events.off('routeChangeComplete', handleRouteChangeComplete)
    }, [dispatch, events])

    // Handle direct navigation to ticket details page and also clicks on back/forward browser's buttons
    useEffect(() => {
        if (!fetching && !state.isRouteTransitioning) {
            if (queryListingId && !state.listing) {
                // Set state based on 'showDetails' query string parameter
                const [listing, listingGroup] = listingSelector(composedListings, queryListingId, queryGroupId)

                if (listing) {
                    toggleDetails({
                        dispatch,
                        listing,
                        listingGroup,
                        targetQuantity: queryQuantity,
                    })
                } else {
                    // Remove URL query parameters if the requested listing cannot be found by ID
                    toggleDetails({ dispatch, listing: null })
                }
            } else if (!queryListingId && state.listing) {
                toggleDetails({ dispatch, listing: null })
            }
        }
    }, [
        fetching,
        composedListings,
        state.listing,
        state.isRouteTransitioning,
        dispatch,
        pathname,
        slug,
        id,
        queryListingId,
        queryGroupId,
        queryQuantity,
    ])

    return (
        <context.Provider
            value={{
                state,
                dispatch: {
                    toggleDetails: (payload: { listing: ComposedListing | null; listingGroup?: ListingsGroup }) => {
                        if (!!state.listing) {
                            removeUrlParameters(pathname, slug, id)
                        }
                        toggleDetails({
                            dispatch,
                            listing: payload.listing,
                            listingGroup: payload.listingGroup,
                            targetQuantity: filterQuantity,
                        })
                    },
                    updateQuantity: (payload: { newQuantity: string }) =>
                        updateQuantity(
                            state,
                            dispatch,
                            pathname,
                            slug,
                            id,
                            queryListingId,
                            queryGroupId,
                            payload.newQuantity,
                        ),
                },
            }}
        >
            {children}
        </context.Provider>
    )
}

export default context
